import React from "react";
import { isString } from "lodash";
import styled from "styled-components";
import { FontWeight } from "@glossy";
import { prettyScrollbars } from "@glossy/mixins";
import { Spacing } from "@glossy/spacings";
import { content, dominant, message } from "@glossy/theme";
import Icon, { Glyph } from "@ui-kit/icon";
import SafeHTML from "@ui-kit/safe-html";
import Title from "./title";

interface IProps {
  // Embeded elements
  children?: React.ReactNode;
  // Big illustration in header
  drawing?: Glyph;
  // Modal title
  title?: string | React.ReactNode;
  // Description text
  description?: string | React.ReactNode;
  // Help text
  help?: string;
  // A bold warning message
  warning?: React.ReactNode;
  // Replaces whole content, except close icon
  customContent?: React.ReactNode;
}

const Description = styled(SafeHTML)`
  color: ${(props) => content(props, "secondary")};
`;

const Content = styled.div<{ hasCustomContent: boolean }>`
  padding: ${(props) => !props.hasCustomContent && Spacing.Large};
  ${prettyScrollbars};
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  row-gap: ${Spacing.Large};
`;

const Warning = styled.div`
  margin-top: 0;
  font-weight: ${FontWeight.Bold};
  color: ${(props) => message(props, "error")};
`;

const Help = styled.div`
  a {
    font-style: italic;
    color: ${dominant};
  }
`;

const UiKitModalContent = ({
  children,
  drawing,
  title,
  description,
  warning,
  help,
  customContent,
}: IProps) => (
  <Content hasCustomContent={!!customContent}>
    {!!drawing && <Icon glyph={drawing} size={96} />}
    {!!title && <Title title={title} />}
    {!!description && <Description html={description} />}
    {!!warning &&
      (isString(warning) ? <SafeHTML as={Warning} html={warning} /> : warning)}
    {children}
    {!!help && <SafeHTML as={Help} html={help} />}
  </Content>
);

export default UiKitModalContent;
